// src/pages/Ambulance.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";

/* -------------------------
   Helper: Load Google Maps
   ------------------------- */
function loadGoogleMaps(apiKey) {
  return new Promise((resolve, reject) => {
    if (!apiKey) return reject(new Error("Google Maps API key not provided."));
    if (window.google && window.google.maps && window.google.maps.places) return resolve(window.google);
    const existing = document.getElementById("google-maps-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () => reject(new Error("Google Maps failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.id = "google-maps-script";
    // include places library; migration uses google.maps.importLibrary("places") if available
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error("Google Maps failed to load"));
    document.head.appendChild(s);
  });
}

/* -------------------------
   AddAmbulance Modal (with UHID search from OT)
   ------------------------- */
const AddAmbulance = ({ item = null, show = false, onClose = () => {}, onSaved = () => {} }) => {
  const modalRef = useRef(null);

  // container refs (we render an input inside the container by default,
  // but may replace it with the PlaceAutocompleteElement if available)
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const [visibleFallback, setVisibleFallback] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ambulance_id: "",
    ambulance_uhid: "",
    ambulance_patient_name: "",
    ambulance_from: "",
    ambulance_to: "",
    ambulance_amt: "",
    ambulance_km: "",
    ambulance_user: ""
  });
  const [uhidSuggestions, setUhidSuggestions] = useState([]);
  const [uhidErrorShown, setUhidErrorShown] = useState(false);
  const [fromPlace, setFromPlace] = useState(null);
  const [toPlace, setToPlace] = useState(null);

  // populate ambulance_user
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("ldata");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.user_id) setForm(f => ({ ...f, ambulance_user: String(parsed.user_id) }));
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (item) {
      setForm({
        ambulance_id: item.ambulance_id || "",
        ambulance_uhid: item.ambulance_uhid || "",
        ambulance_patient_name: item.ambulance_patient_name || "",
        ambulance_from: item.ambulance_from || "",
        ambulance_to: item.ambulance_to || "",
        ambulance_amt: item.ambulance_amt || "",
        ambulance_km: item.ambulance_km || "",
        ambulance_user: item.ambulance_user || form.ambulance_user || ""
      });
      setUhidSuggestions([]);
      setFromPlace(null);
      setToPlace(null);
    } else {
      setForm(f => ({
        ...f,
        ambulance_id: "",
        ambulance_uhid: "",
        ambulance_patient_name: "",
        ambulance_from: "",
        ambulance_to: "",
        ambulance_amt: "",
        ambulance_km: ""
      }));
      setUhidSuggestions([]);
      setFromPlace(null);
      setToPlace(null);
    }
    // eslint-disable-next-line
  }, [item]);

  // bootstrap modal fallback show/hide
  useEffect(() => {
    const b = window.bootstrap;
    if (b && modalRef.current) {
      let inst = b.Modal.getInstance(modalRef.current);
      if (!inst) inst = new b.Modal(modalRef.current, { backdrop: 'static' });
      if (show) inst.show();
      else inst.hide();
      const handler = () => onClose(false);
      modalRef.current.addEventListener('hidden.bs.modal', handler);
      return () => modalRef.current.removeEventListener('hidden.bs.modal', handler);
    } else {
      setVisibleFallback(show);
    }
  }, [show, onClose]);

  // init google places on show — try new PlaceAutocompleteElement first, fallback to legacy Autocomplete
  useEffect(() => {
    if (!show) return;
    const key = AppConst.GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    let mounted = true;

    // store references so we can remove listeners/instances on cleanup
    let placeFromEl = null;
    let placeToEl = null;
    let acFrom = null;
    let acTo = null;

    (async () => {
      try {
        const google = await loadGoogleMaps(key);
        if (!mounted) return;

        // Attempt to import the new places library (if runtime supports importLibrary)
        try {
          if (google.maps.importLibrary) {
            // importLibrary returns a promise that resolves to the requested library namespace in some runtimes
            // but it MAY also just ensure the library is loaded; we still guard against missing APIs
            try {
              await google.maps.importLibrary("places");
            } catch (e) {
              // some builds return immediately or don't require await; ignore failures here
            }
          }

          // If the new web component/element is available, use it
          if (google.maps.places && google.maps.places.PlaceAutocompleteElement) {
            // FROM
            if (fromRef.current) {
              // clear container and attach the PlaceAutocompleteElement which will render its own input
              fromRef.current.innerHTML = '';
              try {
                placeFromEl = new google.maps.places.PlaceAutocompleteElement();
                fromRef.current.appendChild(placeFromEl);

                // listen for selection (gmp-select). event shape can vary; be defensive.
                const fromHandler = async (ev) => {
                  try {
                    // event may be CustomEvent with detail or a plain object: check both
                    const detail = ev?.detail || ev;
                    const placePrediction = detail?.placePrediction || detail?.place;
                    // placePrediction may expose toPlace(); guard accordingly
                    const toPlaceObj = placePrediction && typeof placePrediction.toPlace === 'function'
                      ? placePrediction.toPlace()
                      : null;

                    if (toPlaceObj && typeof toPlaceObj.fetchFields === 'function') {
                      // fetch the fields we need (may vary by runtime)
                      await toPlaceObj.fetchFields({ fields: ['formattedAddress', 'displayName', 'location'] });
                      const json = typeof toPlaceObj.toJSON === 'function' ? toPlaceObj.toJSON() : {};
                      const addr = json.formattedAddress || json.displayName || '';
                      const loc = json.location || {};
                      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
                      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
                      setFromPlace({ formatted_address: addr, lat, lng });
                      setForm(prev => ({ ...prev, ambulance_from: addr }));
                    } else if (detail?.placePrediction?.description || detail?.placePrediction?.structured_formatting) {
                      // fallback: try to read description or description-like field
                      const addr = detail.placePrediction.description || detail.placePrediction.structured_formatting?.main_text || '';
                      setFromPlace({ formatted_address: addr, lat: null, lng: null });
                      setForm(prev => ({ ...prev, ambulance_from: addr }));
                    }
                  } catch (errInner) {
                    console.warn('PlaceAutocompleteElement (from) select handler error', errInner);
                  }
                };

                placeFromEl.addEventListener && placeFromEl.addEventListener('gmp-select', fromHandler);
                // store handler on element so cleanup can remove it
                placeFromEl.__gmp_select_handler = fromHandler;
              } catch (errCreate) {
                console.warn('create PlaceAutocompleteElement (from) failed', errCreate);
                // fallback will be attempted below in catch
                throw errCreate;
              }
            }

            // TO
            if (toRef.current) {
              toRef.current.innerHTML = '';
              try {
                placeToEl = new google.maps.places.PlaceAutocompleteElement();
                toRef.current.appendChild(placeToEl);

                const toHandler = async (ev) => {
                  try {
                    const detail = ev?.detail || ev;
                    const placePrediction = detail?.placePrediction || detail?.place;
                    const toPlaceObj = placePrediction && typeof placePrediction.toPlace === 'function'
                      ? placePrediction.toPlace()
                      : null;
                    if (toPlaceObj && typeof toPlaceObj.fetchFields === 'function') {
                      await toPlaceObj.fetchFields({ fields: ['formattedAddress', 'displayName', 'location'] });
                      const json = typeof toPlaceObj.toJSON === 'function' ? toPlaceObj.toJSON() : {};
                      const addr = json.formattedAddress || json.displayName || '';
                      const loc = json.location || {};
                      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
                      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
                      setToPlace({ formatted_address: addr, lat, lng });
                      setForm(prev => ({ ...prev, ambulance_to: addr }));
                    } else if (detail?.placePrediction?.description) {
                      const addr = detail.placePrediction.description;
                      setToPlace({ formatted_address: addr, lat: null, lng: null });
                      setForm(prev => ({ ...prev, ambulance_to: addr }));
                    }
                  } catch (errInner) {
                    console.warn('PlaceAutocompleteElement (to) select handler error', errInner);
                  }
                };

                placeToEl.addEventListener && placeToEl.addEventListener('gmp-select', toHandler);
                placeToEl.__gmp_select_handler = toHandler;
              } catch (errCreate2) {
                console.warn('create PlaceAutocompleteElement (to) failed', errCreate2);
                throw errCreate2;
              }
            }

            // Done using new element
            return;
          } else {
            // new element not present — throw to jump to the catch that uses legacy Autocomplete
            throw new Error('PlaceAutocompleteElement not present');
          }
        } catch (errNew) {
          // Fall back to legacy Autocomplete (older API). This is the behavior you had previously.
          console.info('PlaceAutocompleteElement not available — falling back to legacy Autocomplete.', errNew);

          try {
            // Ensure containers have an INPUT (if you replaced innerHTML earlier, create one now)
            if (fromRef.current) {
              // find existing input inside container, or create one
              let inputFrom = fromRef.current.querySelector('input');
              if (!inputFrom) {
                inputFrom = document.createElement('input');
                inputFrom.type = 'text';
                inputFrom.className = 'form-control';
                inputFrom.placeholder = 'Pickup address';
                fromRef.current.appendChild(inputFrom);
              }
              // set initial value
              inputFrom.value = form.ambulance_from || '';
              // create legacy Autocomplete
              acFrom = new google.maps.places.Autocomplete(inputFrom, { fields: ['formatted_address', 'geometry', 'name'] });
              acFrom.addListener('place_changed', () => {
                try {
                  const p = acFrom.getPlace();
                  if (p && p.geometry) {
                    const lat = p.geometry.location.lat();
                    const lng = p.geometry.location.lng();
                    setFromPlace({ formatted_address: p.formatted_address || p.name, lat, lng });
                    setForm(prev => ({ ...prev, ambulance_from: p.formatted_address || p.name }));
                  } else {
                    // If no geometry, just set formatted address if available
                    setForm(prev => ({ ...prev, ambulance_from: p.formatted_address || p.name || inputFrom.value }));
                  }
                } catch (errPC) {
                  console.warn('legacy acFrom place_changed handler', errPC);
                }
              });
            }

            if (toRef.current) {
              let inputTo = toRef.current.querySelector('input');
              if (!inputTo) {
                inputTo = document.createElement('input');
                inputTo.type = 'text';
                inputTo.className = 'form-control';
                inputTo.placeholder = 'Destination address';
                toRef.current.appendChild(inputTo);
              }
              inputTo.value = form.ambulance_to || '';
              acTo = new google.maps.places.Autocomplete(inputTo, { fields: ['formatted_address', 'geometry', 'name'] });
              acTo.addListener('place_changed', () => {
                try {
                  const p = acTo.getPlace();
                  if (p && p.geometry) {
                    const lat = p.geometry.location.lat();
                    const lng = p.geometry.location.lng();
                    setToPlace({ formatted_address: p.formatted_address || p.name, lat, lng });
                    setForm(prev => ({ ...prev, ambulance_to: p.formatted_address || p.name }));
                  } else {
                    setForm(prev => ({ ...prev, ambulance_to: p.formatted_address || p.name || inputTo.value }));
                  }
                } catch (errPC2) {
                  console.warn('legacy acTo place_changed handler', errPC2);
                }
              });
            }
          } catch (legacyErr) {
            console.warn('Legacy Autocomplete init failed', legacyErr);
          }
        }
      } catch (loadErr) {
        console.warn('Google Maps load failed', loadErr);
      }
    })();

    // cleanup: remove appended elements / listeners
    return () => {
      mounted = false;
      try {
        if (placeFromEl) {
          try {
            const h = placeFromEl.__gmp_select_handler;
            if (h) placeFromEl.removeEventListener && placeFromEl.removeEventListener('gmp-select', h);
          } catch (e) {}
          try { placeFromEl.remove(); } catch (e) {}
          placeFromEl = null;
        }
        if (placeToEl) {
          try {
            const h = placeToEl.__gmp_select_handler;
            if (h) placeToEl.removeEventListener && placeToEl.removeEventListener('gmp-select', h);
          } catch (e) {}
          try { placeToEl.remove(); } catch (e) {}
          placeToEl = null;
        }
        if (acFrom && typeof acFrom.unbindAll === 'function') {
          try { acFrom.unbindAll(); } catch (e) {}
        }
        if (acTo && typeof acTo.unbindAll === 'function') {
          try { acTo.unbindAll(); } catch (e) {}
        }
      } catch (e) {
        // ignore cleanup errors
      }
    };
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  // compute km when both places present
  useEffect(() => {
    if (!fromPlace || !toPlace) return;
    if (!window.google || !window.google.maps || !window.google.maps.DistanceMatrixService) return;
    try {
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix({
        origins: [new window.google.maps.LatLng(fromPlace.lat, fromPlace.lng)],
        destinations: [new window.google.maps.LatLng(toPlace.lat, toPlace.lng)],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC
      }, (response, status) => {
        if (status !== 'OK') {
          console.warn('DistanceMatrix status', status);
          return;
        }
        const el = response.rows?.[0]?.elements?.[0];
        if (el && el.status === 'OK') {
          const meters = el.distance.value || 0;
          const km = +(meters / 1000).toFixed(2);
          setForm(prev => ({ ...prev, ambulance_km: String(km) }));
        }
      });
    } catch (err) {
      console.warn('DistanceMatrix error', err);
    }
  }, [fromPlace, toPlace]);

  const hideModalFallback = () => {
    setVisibleFallback(false);
    onClose(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  /* -------------------------
     UHID search (reuse your OT search)
     ------------------------- */
  const searchUhidNow = async (val) => {
    const uhid = (val ?? form.ambulance_uhid ?? "").trim();
    if (!uhid || uhid.length < 3) {
      setUhidSuggestions([]);
      setUhidErrorShown(false);
      return;
    }
    try {
      const fd = new FormData();
      fd.append("lab_reg_uhid", uhid);
      fd.append("search_type", "IPD");
      const resp = await axios.post(AppConst.BASE_URL + AppConst.searchUhidUrlCall, fd);
      if (resp.data?.status && Array.isArray(resp.data.records) && resp.data.records.length > 0) {
        const ipd = resp.data.records.filter(r => r.lab_reg_type === "IPD" || !!r.ipd_id || !!r.ipd_uhid);
        setUhidSuggestions(ipd.length ? ipd : resp.data.records);
        setUhidErrorShown(false);
      } else {
        setUhidSuggestions([]);
        if (!uhidErrorShown) {
          toast.error("UHID does not exist in IPD!");
          setUhidErrorShown(true);
        }
      }
    } catch (err) {
      console.error("UHID search error:", err);
      setUhidSuggestions([]);
      if (!uhidErrorShown) {
        toast.error("UHID search error");
        setUhidErrorShown(true);
      }
    }
  };

  const handleSelectUhidSuggestion = async (rec) => {
    if (!rec) return;
    const ipdUhid = rec.ipd_uhid || rec.lab_reg_uhid || rec.lab_reg_uhid_no || rec.uhid || "";
    const ipdId = rec.ipd_id || rec.lab_reg_id || rec.ipd_no || rec.id || rec.lab_reg_id || "";
    const patientName = rec.ipd_patient_name || rec.lab_reg_name || rec.name || "";
    const bed = rec.ipd_bed || rec.lab_bed || rec.bed || "";
    const patientAddress = rec.ipd_address || rec.lab_reg_address || rec.address || "";
    const patientMobile = rec.ipd_patient_mobile || rec.lab_reg_mobile || rec.mobile || "";
    setForm(prev => ({
      ...prev,
      ambulance_uhid: ipdUhid || prev.ambulance_uhid,
      ambulance_patient_name: patientName || prev.ambulance_patient_name,
      ambulance_from: patientAddress || prev.ambulance_from,
    }));
    setUhidSuggestions([]);
    setUhidErrorShown(false);
    toast.success("Patient data fetched.");
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    if (!form.ambulance_patient_name || form.ambulance_patient_name.trim() === "") {
      toast.error("Please enter patient name or select via UHID.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      if (form.ambulance_id) fd.append('ambulance_id', form.ambulance_id);
      fd.append('ambulance_uhid', form.ambulance_uhid || '');
      fd.append('ambulance_patient_name', form.ambulance_patient_name || '');
      fd.append('ambulance_from', form.ambulance_from || '');
      fd.append('ambulance_to', form.ambulance_to || '');
      fd.append('ambulance_amt', form.ambulance_amt || 0);
      fd.append('ambulance_km', form.ambulance_km || '');
      fd.append('ambulance_user', form.ambulance_user || '1');
      const res = await axios.post(AppConst.BASE_URL + (AppConst.addAmbulanceUrl || "/"), fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data && res.data.status) {
        toast.success(res.data.success || (form.ambulance_id ? 'Updated' : 'Added'));
        const b = window.bootstrap;
        if (b && modalRef.current) {
          const inst = b.Modal.getInstance(modalRef.current);
          if (inst) inst.hide();
        } else {
          hideModalFallback();
        }
        onSaved(true);
      } else {
        toast.error(res.data?.error || 'Save failed');
      }
    } catch (err) {
      console.error('AddAmbulance save error', err);
      toast.error('Server error saving ambulance request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div ref={modalRef} className={`modal fade ${visibleFallback ? 'show' : ''}`} tabIndex="-1" role="dialog" style={visibleFallback ? { display: 'block' } : {}} aria-hidden={!visibleFallback}>
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <form onSubmit={handleSave}>
              <div className="modal-header">
                <h5 className="modal-title">{form.ambulance_id ? 'Edit Ambulance Request' : 'Add Ambulance Request'}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={hideModalFallback}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-4">
                    <label className="form-label">UHID</label>
                    <input
                      type="text"
                      name="ambulance_uhid"
                      value={form.ambulance_uhid}
                      onChange={(e) => { handleChange(e); searchUhidNow(e.target.value); }}
                      className="form-control"
                      placeholder="Enter UHID (min 3 chars)"
                      autoComplete="off"
                    />
                    {uhidSuggestions.length > 0 && (
                      <ul className="list-group position-absolute w-100" style={{ zIndex: 9999 }}>
                        {uhidSuggestions.map((u, i) => (
                          <li key={i} className="list-group-item list-group-item-action" style={{ cursor: 'pointer' }}
                              onClick={() => handleSelectUhidSuggestion(u)}>
                            {u.lab_reg_uhid || u.ipd_uhid || ''} - {u.lab_reg_name || u.ipd_patient_name || u.name || ''}
                            <span className="badge bg-info ms-2">{u.lab_reg_type || (u.ipd_id ? "IPD" : "")}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="col-md-8">
                    <label className="form-label">Patient Name</label>
                    <input type="text" name="ambulance_patient_name" value={form.ambulance_patient_name} onChange={handleChange} className="form-control" />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">From</label>
                    {/* container: we will either append the new PlaceAutocompleteElement here, or render an input inside */}
                    <div ref={fromRef}>
                      {/* default input (will be replaced by the element if available) */}
                      <input type="text" name="ambulance_from" value={form.ambulance_from} onChange={handleChange} className="form-control" placeholder="Pickup address" />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">To</label>
                    <div ref={toRef}>
                      <input type="text" name="ambulance_to" value={form.ambulance_to} onChange={handleChange} className="form-control" placeholder="Destination address" />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Distance (KM)</label>
                    <input type="text" name="ambulance_km" value={form.ambulance_km} onChange={handleChange} readOnly className="form-control" />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Amount</label>
                    <input type="number" name="ambulance_amt" value={form.ambulance_amt} onChange={handleChange} className="form-control" />
                  </div>

                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={hideModalFallback}>Close</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : (form.ambulance_id ? 'Update' : 'Save')}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {visibleFallback && <div className="modal-backdrop fade show" />}
    </>
  );
};

/* -------------------------
   Export helpers (CSV & PDF)
   ------------------------- */
const downloadCsv = (rows = [], filename = 'ambulance.csv') => {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(','),
    ...rows.map(r => keys.map(k => {
      const val = r[k] === null || r[k] === undefined ? '' : String(r[k]).replace(/"/g, '""');
      return `"${val}"`;
    }).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportPdfFromRows = (rows = [], fileNamePrefix = 'ambulance') => {
  const doc = new jsPDF('p', 'pt', 'a4');
  doc.setFontSize(14);
  doc.text('Ambulance Requests', 40, 40);
  const head = [['Sl. No.', 'UHID', 'Patient', 'From', 'To', 'KM', 'Amount']];
  const body = rows.map((r, idx) => [
    String(idx + 1),
    r.ambulance_uhid || '',
    r.ambulance_patient_name || '',
    r.ambulance_from || '',
    r.ambulance_to || '',
    r.ambulance_km || '',
    r.ambulance_amt !== undefined && r.ambulance_amt !== null ? String(r.ambulance_amt) : ''
  ]);
  autoTable(doc, {
    startY: 60,
    head,
    body,
    styles: { fontSize: 10, cellPadding: 4 },
    headStyles: { fillColor: [240, 240, 240] },
    margin: { left: 40, right: 40 },
    theme: 'grid',
  });
  doc.save(`${fileNamePrefix}_${Date.now()}.pdf`);
};

/* -------------------------
   Main Ambulance Component
   ------------------------- */
const Ambulance = () => {
  const [formData, setFormData] = useState({ l1: [], page_no: 0, search_key: '' });
  const [isAdmin, setIsAdmin] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const mountedRef = useRef(false);

  const getLoggedUserId = () => {
    try {
      const s = sessionStorage.getItem('ldata');
      if (!s) return '1';
      const p = JSON.parse(s);
      return p?.user_id ? String(p.user_id) : '1';
    } catch (e) { return '1'; }
  };

  const initData = async () => {
    try {
      const storedData = sessionStorage.getItem('ldata');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setIsAdmin(parsed?.role_id === 1 || parsed?.role_id === '1');
      }
      const fd = new FormData();
      fd.append('page_no', formData.page_no + '');
      fd.append('search_key', formData.search_key + '');
      fd.append('ambulance_user', getLoggedUserId());
      const response = await axios.post(AppConst.BASE_URL + AppConst.getAmbulanceListUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data && response.data.status) {
        setFormData(prev => ({ ...prev, l1: Array.isArray(response.data.records) ? response.data.records : [] }));
      } else {
        setFormData(prev => ({ ...prev, l1: [] }));
      }
    } catch (err) {
      console.error('initData error', err);
      setFormData(prev => ({ ...prev, l1: [] }));
    }
  };

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      initData();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    initData();
    // eslint-disable-next-line
  }, [formData.search_key, formData.page_no]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const delItem = async (row) => {
    if (!isAdmin) { toast.error('Only admin can delete.'); return; }
    const result = await Swal.fire({
      title: `Delete request for ${row.ambulance_patient_name || 'this patient'}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('ambulance_id', row.ambulance_id + '');
      const response = await axios.post(AppConst.BASE_URL + AppConst.delAmbulanceUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data && response.data.status) {
        toast.success('Deleted');
        initData();
      } else {
        toast.error(response.data?.error || 'Delete failed');
      }
    } catch (err) {
      console.error('delItem error', err);
      toast.error('Delete failed');
    }
  };

  const openAddModal = () => { setEditItem(null); setShowModal(true); };
  const handleEdit = (row) => { setEditItem(row); setShowModal(true); };

  const filteredRows = useMemo(() => {
    const s = (formData.search_key || '').trim().toLowerCase();
    if (!s) return formData.l1 || [];
    return (formData.l1 || []).filter(r =>
      (r.ambulance_uhid || '').toString().toLowerCase().includes(s) ||
      (r.ambulance_patient_name || '').toString().toLowerCase().includes(s) ||
      (r.ambulance_from || '').toString().toLowerCase().includes(s) ||
      (r.ambulance_to || '').toString().toLowerCase().includes(s) ||
      (r.ambulance_amt || '').toString().toLowerCase().includes(s)
    );
  }, [formData.l1, formData.search_key]);

  // Put this where you currently define `columns` and `customStyles`

const wrapCellStyle = {
  whiteSpace: 'normal',       // allow wrapping
  wordBreak: 'break-word',    // break long words/URLs
  overflowWrap: 'anywhere',   // more aggressive wrapping for long strings
  lineHeight: '1.3',
  padding: '6px 8px'
};

const columns = useMemo(() => ([
  { name: 'Sl. No.', selector: (row, idx) => idx + 1, width: '80px', center: true },
  { name: 'UHID', 
    // use cell renderer so long UHIDs also wrap if necessary
    cell: row => <div style={wrapCellStyle}>{row.ambulance_uhid || ''}</div>,
    sortable: true, width: '140px' 
  },
  { name: 'Patient', 
    cell: row => <div style={wrapCellStyle}>{row.ambulance_patient_name || ''}</div>,
    sortable: true, grow: 1 
  },
  { name: 'From', 
    cell: row => <div style={wrapCellStyle}>{row.ambulance_from || ''}</div>,
    width: '200px'
  },
  { name: 'To', 
    cell: row => <div style={wrapCellStyle}>{row.ambulance_to || ''}</div>,
    width: '200px'
  },
  { name: 'KM', selector: row => row.ambulance_km || '', sortable: true, width: '90px' },
  { name: 'Amount', selector: row => row.ambulance_amt || '', sortable: true, right: true, width: '120px' },
  {
    name: 'Action',
    cell: row => (
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(row)} disabled={!isAdmin} style={{ opacity: !isAdmin ? 0.5 : 1 }}>Edit</button>
        <button className="btn btn-sm btn-outline-danger" onClick={() => delItem(row)} disabled={!isAdmin} style={{ opacity: !isAdmin ? 0.5 : 1 }}>Delete</button>
      </div>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: '180px'
  }
]), [isAdmin]); // keep dependencies as before

const customStyles = {
  headCells: { style: { backgroundColor: '#f1f5fb', color: '#405189', fontSize: '14px', fontWeight: '700' } },
  rows: { style: { minHeight: 'auto' } },            // let rows grow
  cells: { style: { paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'normal' } } // enable wrapping globally
};


  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row"><div className="col-lg-12">
          <div className="card" id="orderList">
            <div className="card-header border-0 d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Ambulance Requests</h5>
              <div>
                <button className="btn btn-success me-2" onClick={openAddModal}><i className="ri-add-line align-bottom me-1" /> Add Ambulance</button>
                <button className="btn btn-outline-secondary me-2" onClick={() => downloadCsv(filteredRows, 'ambulance.csv')}>Export CSV</button>
                <button className="btn btn-outline-primary" onClick={() => exportPdfFromRows(filteredRows, 'ambulance')}>Export PDF</button>
              </div>
            </div>

            <div className="card-body border border-dashed border-end-0 border-start-0">
              <form>
                <div className="row g-3">
                  <div className="col-xxl-5 col-sm-6">
                    <div className="search-box">
                      <input className="form-control search" name="search_key" onChange={handleChange} value={formData.search_key} placeholder="Search by UHID, patient, from, to..." type="text" />
                      <i className="ri-search-line search-icon" />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="card-body pt-0">
              <div className="table-responsive table-card mb-1">
                <DataTable columns={columns} data={filteredRows} customStyles={customStyles} noHeader pagination paginationPerPage={10} paginationRowsPerPageOptions={[10,20,50]} highlightOnHover persistTableHead dense={false} />
              </div>

              <div className="d-flex justify-content-end">
                <div className="pagination-wrap hstack gap-2">
                  {/* <a className="page-item pagination-prev disabled" href="#" onClick={(e) => { e.preventDefault(); if (formData.page_no > 0) setFormData(prev => ({ ...prev, page_no: prev.page_no - 1 })); }}>Previous</a> */}
                  <ul className="pagination listjs-pagination mb-0" />
                  {/* <a className="page-item pagination-next" href="#" onClick={(e) => { e.preventDefault(); setFormData(prev => ({ ...prev, page_no: prev.page_no + 1 })); }}>Next</a> */}
                </div>
              </div>

              <AddAmbulance item={editItem} show={showModal} onClose={(ref) => { setShowModal(false); setEditItem(null); if (ref) initData(); }} onSaved={() => { setShowModal(false); setEditItem(null); initData(); }} />

            </div>
          </div>
        </div></div>
      </div>
    </div>
  );
};

export default Ambulance;
